import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  FileText,
} from "lucide-react";

type MaterialLine = {
  id: string;
  material_name: string;
  quantity: string;
  uom: string;
};

type BolOption = {
  id: number;
  bol_number: string;
  date: string;
};

const COMMON_MATERIALS = [
  "Corn Dogs",
  "Frozen Foods",
  "Poultry",
  "Produce",
  "Industrial Waste",
  "Organic Waste",
  "Mixed Food Products",
];

const UOM_OPTIONS = [
  { value: "lbs", label: "lbs" },
  { value: "tons", label: "tons" },
  { value: "cubic_yards", label: "cubic yards" },
  { value: "pallets", label: "pallets" },
  { value: "boxes", label: "boxes" },
  { value: "bags", label: "bags" },
  { value: "gallons", label: "gallons" },
  { value: "units", label: "units" },
];

const LOCATIONS = [
  { value: "Congress", label: "Congress, AZ" },
  { value: "Phoenix", label: "Phoenix, AZ" },
];

function generateId() {
  return Math.random().toString(36).slice(2, 10);
}

export default function CreateCod() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Receipt Info
  const [dateReceived, setDateReceived] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [receivedFrom, setReceivedFrom] = useState("");
  const [destructionLocation, setDestructionLocation] = useState("Congress");

  // Customer References
  const [salesOrder, setSalesOrder] = useState("");
  const [freightOrder, setFreightOrder] = useState("");
  const [vanguardWorkOrder, setVanguardWorkOrder] = useState("");

  // Association
  const [clientTag, setClientTag] = useState("");
  const [linkedBolId, setLinkedBolId] = useState("");
  const [recentBols, setRecentBols] = useState<BolOption[]>([]);

  // Materials
  const [materials, setMaterials] = useState<MaterialLine[]>([
    { id: generateId(), material_name: "", quantity: "", uom: "lbs" },
  ]);

  // Authorization
  const [authName, setAuthName] = useState("");
  const [authTitle, setAuthTitle] = useState("");
  const [authDate, setAuthDate] = useState(
    new Date().toISOString().slice(0, 10)
  );

  // Notes
  const [notes, setNotes] = useState("");

  useEffect(() => {
    apiFetch("/api/ops/bols?limit=20", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (res.ok) return res.json();
        return { bols: [] };
      })
      .then((data) => {
        setRecentBols(data.bols || []);
      })
      .catch(() => {});
  }, [token]);

  const addMaterial = () => {
    setMaterials((prev) => [
      ...prev,
      { id: generateId(), material_name: "", quantity: "", uom: "lbs" },
    ]);
  };

  const removeMaterial = (id: string) => {
    setMaterials((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((m) => m.id !== id);
    });
  };

  const updateMaterial = (id: string, field: keyof MaterialLine, value: string) => {
    setMaterials((prev) =>
      prev.map((m) => (m.id === id ? { ...m, [field]: value } : m))
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!receivedFrom.trim()) {
      setError("Received From is required.");
      return;
    }

    const validMaterials = materials.filter((m) => m.material_name.trim());
    if (validMaterials.length === 0) {
      setError("At least one material is required.");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        date_received: dateReceived,
        received_from: receivedFrom.trim(),
        destruction_location: destructionLocation,
        sales_order: salesOrder.trim() || null,
        freight_order: freightOrder.trim() || null,
        vanguard_work_order: vanguardWorkOrder.trim() || null,
        client_tag: clientTag || null,
        linked_bol_id: linkedBolId ? Number(linkedBolId) : null,
        materials: validMaterials.map((m) => ({
          material_name: m.material_name.trim(),
          quantity: m.quantity ? Number(m.quantity) : null,
          uom: m.uom,
        })),
        authorization: {
          name: authName.trim() || null,
          title: authTitle.trim() || null,
          date: authDate || null,
        },
        notes: notes.trim() || null,
      };

      const res = await apiFetch("/api/ops/cods", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to create COD (${res.status})`);
      }

      navigate("/app/ops/cods");
    } catch (err: any) {
      setError(err?.message || "Failed to create COD");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/app/ops/cods")}
          className="p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="heading-display text-3xl text-foreground">New Certificate of Destruction</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create a new COD to document material destruction.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Receipt Info */}
        <div className="bg-card border border-border rounded-xl p-6 space-y-4">
          <h2 className="text-sm font-bold text-foreground uppercase tracking-wide flex items-center gap-2">
            <FileText size={16} className="text-forest-600 dark:text-forest-400" />
            Receipt Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                Date Received *
              </label>
              <input
                type="date"
                value={dateReceived}
                onChange={(e) => setDateReceived(e.target.value)}
                required
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                Received From *
              </label>
              <input
                type="text"
                value={receivedFrom}
                onChange={(e) => setReceivedFrom(e.target.value)}
                placeholder="Company or individual name"
                required
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                Destruction Location
              </label>
              <select
                value={destructionLocation}
                onChange={(e) => setDestructionLocation(e.target.value)}
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
              >
                {LOCATIONS.map((loc) => (
                  <option key={loc.value} value={loc.value}>
                    {loc.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Customer References */}
        <div className="bg-card border border-border rounded-xl p-6 space-y-4">
          <h2 className="text-sm font-bold text-foreground uppercase tracking-wide">
            Customer References
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                Sales Order #
              </label>
              <input
                type="text"
                value={salesOrder}
                onChange={(e) => setSalesOrder(e.target.value)}
                placeholder="SO-12345"
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                Freight Order #
              </label>
              <input
                type="text"
                value={freightOrder}
                onChange={(e) => setFreightOrder(e.target.value)}
                placeholder="FO-12345"
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                Vanguard Work Order #
              </label>
              <input
                type="text"
                value={vanguardWorkOrder}
                onChange={(e) => setVanguardWorkOrder(e.target.value)}
                placeholder="WO-12345"
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
              />
            </div>
          </div>
        </div>

        {/* Association */}
        <div className="bg-card border border-border rounded-xl p-6 space-y-4">
          <h2 className="text-sm font-bold text-foreground uppercase tracking-wide">
            Association
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                Client Tag
              </label>
              <select
                value={clientTag}
                onChange={(e) => setClientTag(e.target.value)}
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
              >
                <option value="">None</option>
                <option value="vanguard">Vanguard</option>
                <option value="willcox">Willcox</option>
                <option value="3lag">3LAG</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                Link to BOL (optional)
              </label>
              <select
                value={linkedBolId}
                onChange={(e) => setLinkedBolId(e.target.value)}
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
              >
                <option value="">No linked BOL</option>
                {recentBols.map((bol) => (
                  <option key={bol.id} value={bol.id}>
                    {bol.bol_number} - {new Date(bol.date).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Materials */}
        <div className="bg-card border border-border rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-foreground uppercase tracking-wide flex items-center gap-2">
              <Plus size={16} className="text-forest-600 dark:text-forest-400" />
              Materials
            </h2>
            <button
              type="button"
              onClick={addMaterial}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-forest-600 hover:bg-forest-700 text-white text-xs font-semibold rounded-lg transition-colors"
            >
              <Plus size={12} />
              Add Material
            </button>
          </div>

          <div className="space-y-3">
            {materials.map((material, index) => (
              <div
                key={material.id}
                className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end p-3 bg-secondary/50 rounded-lg border border-border/60"
              >
                <div className="md:col-span-1 flex items-center justify-center">
                  <span className="text-xs font-bold text-muted-foreground w-6 h-6 rounded-full bg-secondary flex items-center justify-center">
                    {index + 1}
                  </span>
                </div>
                <div className="md:col-span-4">
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Material Name
                  </label>
                  <select
                    value={material.material_name}
                    onChange={(e) =>
                      updateMaterial(material.id, "material_name", e.target.value)
                    }
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                  >
                    <option value="">Select material...</option>
                    {COMMON_MATERIALS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                    <option value="__custom">Other (type below)</option>
                  </select>
                  {material.material_name === "__custom" && (
                    <input
                      type="text"
                      placeholder="Enter material name"
                      onChange={(e) =>
                        updateMaterial(material.id, "material_name", e.target.value)
                      }
                      className="w-full mt-2 px-3 py-2 bg-background border border-border rounded-lg text-sm"
                    />
                  )}
                </div>
                <div className="md:col-span-3">
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={material.quantity}
                    onChange={(e) =>
                      updateMaterial(material.id, "quantity", e.target.value)
                    }
                    placeholder="0"
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Unit of Measure
                  </label>
                  <select
                    value={material.uom}
                    onChange={(e) =>
                      updateMaterial(material.id, "uom", e.target.value)
                    }
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                  >
                    {UOM_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-1 flex items-end justify-center">
                  <button
                    type="button"
                    onClick={() => removeMaterial(material.id)}
                    disabled={materials.length <= 1}
                    className="p-2 rounded-lg text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Remove material"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Authorization */}
        <div className="bg-card border border-border rounded-xl p-6 space-y-4">
          <h2 className="text-sm font-bold text-foreground uppercase tracking-wide">
            Authorization
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                Name
              </label>
              <input
                type="text"
                value={authName}
                onChange={(e) => setAuthName(e.target.value)}
                placeholder="Authorized person"
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                Title
              </label>
              <input
                type="text"
                value={authTitle}
                onChange={(e) => setAuthTitle(e.target.value)}
                placeholder="Operations Manager"
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                Date
              </label>
              <input
                type="date"
                value={authDate}
                onChange={(e) => setAuthDate(e.target.value)}
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-card border border-border rounded-xl p-6 space-y-4">
          <h2 className="text-sm font-bold text-foreground uppercase tracking-wide">
            Notes
          </h2>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder="Additional notes or observations..."
            className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm resize-y"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={() => navigate("/app/ops/cods")}
            className="px-5 py-2.5 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-forest-600/20"
          >
            <Save size={16} />
            {submitting ? "Creating..." : "Create COD"}
          </button>
        </div>
      </form>
    </div>
  );
}
